import React, { useRef, useState, useEffect, useCallback } from 'react';
import Quagga from 'quagga';
import './BarcodeScanner.css';

// Create a more audible beep sound
const playBeepSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      console.log('Web Audio API not supported in this browser');
      return;
    }
    
    const audioContext = new AudioContext();
    
    // Create a new oscillator for each beep
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect the oscillator to the gain node and the gain node to the output
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure the oscillator for a more noticeable beep
    oscillator.type = 'square'; // Square wave for a sharper sound
    oscillator.frequency.value = 800; // Lower frequency (800Hz) is more audible on most speakers
    
    // Set initial volume to 0
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    // Quickly ramp up to full volume
    gainNode.gain.linearRampToValueAtTime(0.7, audioContext.currentTime + 0.01);
    // Hold for most of the duration
    gainNode.gain.setValueAtTime(0.7, audioContext.currentTime + 0.2);
    // Ramp down at the end to avoid clicks
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
    
    // Start the oscillator
    oscillator.start();
    
    // Stop the oscillator and close the context after the beep duration
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 300); // 300ms duration for a more noticeable beep
  } catch (error) {
    console.error('Error playing beep sound:', error);
  }
};

const BarcodeScanner = ({ onDetected, onClose }) => {
  const scannerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState('');
  const [scannerMessage, setScannerMessage] = useState('Position barcode in view');
  const [lastResult, setLastResult] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // default to back camera
  const [isProcessing, setIsProcessing] = useState(false); // Flag to prevent multiple API calls
  const [lastDetectionTime, setLastDetectionTime] = useState(0); // Track last detection time
  const detectionCooldown = 3000; // 3 seconds cooldown between detections

  // Process detected barcode with debouncing
  const processDetectedBarcode = useCallback((code) => {
    // Check if we're already processing a barcode or if this is a duplicate
    if (isProcessing || code === lastResult) {
      return;
    }

    // Check if enough time has passed since the last detection
    const now = Date.now();
    if (now - lastDetectionTime < detectionCooldown) {
      return;
    }

    // Set processing flag and update last detection time
    setIsProcessing(true);
    setLastDetectionTime(now);
    setLastResult(code);
    setScannerMessage('Barcode detected!');
    
    // Vibrate device if supported
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
    
    // Play a more audible beep sound
    playBeepSound();
    
    // Stop scanning
    Quagga.stop();
    
    // Pass the detected barcode to parent component
    onDetected(code);
  }, [isProcessing, lastResult, lastDetectionTime, detectionCooldown, onDetected]);

  // Setup barcode listeners
  const setupBarcodeListeners = useCallback(() => {
    // Listen for barcode detection
    Quagga.onDetected((result) => {
      const code = result.codeResult.code;
      processDetectedBarcode(code);
    });
    
    // Optionally draw detection results on canvas for feedback
    Quagga.onProcessed((result) => {
      const drawingCanvas = document.querySelector('#interactive canvas');
      const drawingContext = drawingCanvas?.getContext('2d');
      
      if (!drawingContext || !result) {
        return;
      }
      
      // Draw barcode location boxes
      if (result.boxes) {
        drawingContext.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);
        
        // Only draw the boxes for potential barcodes
        result.boxes.filter(box => box !== result.box).forEach(box => {
          drawingContext.beginPath();
          drawingContext.lineWidth = 2;
          drawingContext.strokeStyle = "rgba(255, 0, 0, 0.5)";
          drawingContext.moveTo(box[0][0], box[0][1]);
          box.forEach((corner, index) => {
            if (index > 0) {
              drawingContext.lineTo(corner[0], corner[1]);
            }
          });
          drawingContext.lineTo(box[0][0], box[0][1]);
          drawingContext.stroke();
        });
      }
      
      // Draw the matched barcode in green
      if (result.box) {
        drawingContext.beginPath();
        drawingContext.lineWidth = 4;
        drawingContext.strokeStyle = "rgba(0, 255, 0, 0.8)";
        drawingContext.moveTo(result.box[0][0], result.box[0][1]);
        result.box.forEach((corner, index) => {
          if (index > 0) {
            drawingContext.lineTo(corner[0], corner[1]);
          }
        });
        drawingContext.lineTo(result.box[0][0], result.box[0][1]);
        drawingContext.stroke();
      }
    });
  }, [processDetectedBarcode]);

  // Use useCallback to memoize the initBarcodeScanner function
  const initBarcodeScanner = useCallback(() => {
    if (!scannerRef.current) {
      return;
    }

    setError('');
    
    Quagga.init({
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: scannerRef.current,
        constraints: {
          width: { min: 640 },
          height: { min: 480 },
          facingMode: facingMode,
          aspectRatio: { min: 1, max: 2 }
        }
      },
      locator: {
        patchSize: "medium",
        halfSample: true
      },
      numOfWorkers: navigator.hardwareConcurrency || 4,
      decoder: {
        readers: [
          "ean_reader",
          "ean_8_reader",
          "upc_reader",
          "upc_e_reader",
          "code_39_reader",
          "code_128_reader"
        ],
        debug: {
          drawBoundingBox: false,
          showFrequency: false,
          drawScanline: false,
          showPattern: false
        },
        multiple: false // Only detect one barcode at a time
      },
      locate: true,
      frequency: 10 // Reduce scanning frequency to once every 10 frames
    }, (err) => {
      if (err) {
        console.error("Error initializing Quagga:", err);
        
        // Provide user-friendly error message
        let errorMessage = 'Could not initialize barcode scanner. ';
        
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage += 'Camera access was denied. Please allow camera access and try again.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage += 'No camera device found.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage += 'Camera is already in use by another application.';
        } else {
          errorMessage += 'Please check your camera settings and try again.';
        }
        
        setError(errorMessage);
        return;
      }
      
      // Start if successfully initialized
      Quagga.start();
      setIsInitialized(true);
      
      // Set up listeners for detection and processing
      setupBarcodeListeners();
    });
  }, [facingMode, setupBarcodeListeners]);

  // Initialize barcode scanner on component mount
  useEffect(() => {
    initBarcodeScanner();
    
    // Clean up on unmount
    return () => {
      if (isInitialized) {
        Quagga.stop();
      }
    };
  }, [facingMode, initBarcodeScanner, isInitialized]);

  const switchCamera = () => {
    // Stop current scanner
    if (isInitialized) {
      Quagga.stop();
      setIsInitialized(false);
    }
    
    // Toggle camera mode
    setFacingMode(prevMode => prevMode === 'environment' ? 'user' : 'environment');
  };

  return (
    <div className="barcode-scanner-container">
      {error ? (
        <div className="scanner-error">
          <p>{error}</p>
          <div className="scanner-controls">
            <button className="scanner-button retry" onClick={initBarcodeScanner}>
              <i className="fas fa-redo"></i> Retry
            </button>
            <button className="scanner-button close" onClick={onClose}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
      ) : (
        <>
          <div 
            ref={scannerRef} 
            className="scanner-view"
            id="interactive"
          >
            <div className="scanner-overlay">
              <div className="scanner-target"></div>
              <p className="scanner-message">{scannerMessage}</p>
            </div>
          </div>
          
          <div className="scanner-controls">
            <button 
              className="scanner-button switch" 
              onClick={switchCamera}
              title="Switch Camera"
            >
              <i className="fas fa-sync"></i>
            </button>
            
            <button 
              className="scanner-button close" 
              onClick={onClose}
              title="Close Scanner"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BarcodeScanner;